import { AlertCircle, Hand, LoaderCircle, PartyPopper, RotateCcw, Sparkles, ZoomIn, ZoomOut } from 'lucide-preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type * as Three from 'three';
import type { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { CharacterClass } from '../types';

type CharacterAnimation = 'IDLE' | 'WAVE' | 'CELEBRATE';

type CharacterAvatar3DProps = {
  characterClass: CharacterClass;
  appearanceVariant: 'SAGE' | 'SKY' | 'LILAC' | 'EMBER';
  level: number;
  fallbackImage: string;
};

const palette = {
  SAGE: { primary: 0x557967, secondary: 0xe2eee4 },
  SKY: { primary: 0x5f8798, secondary: 0xe4f1f4 },
  LILAC: { primary: 0x786b93, secondary: 0xebe6f3 },
  EMBER: { primary: 0xa36b56, secondary: 0xf5e6dc }
} as const;

const modelByClass: Record<CharacterClass, string> = {
  WAYFINDER: '/assets/models/wayfinder.glb',
  SCHOLAR: '/assets/models/scholar.glb',
  MAKER: '/assets/models/maker.glb',
  WARDEN: '/assets/models/warden.glb'
};

const classAccent: Record<CharacterClass, number> = {
  WAYFINDER: 0x79ad9c,
  WARDEN: 0x8eb97b,
  MAKER: 0xa494d0,
  SCHOLAR: 0xe9a1ad
};

export function CharacterAvatar3D({ characterClass, appearanceVariant, level, fallbackImage }: CharacterAvatar3DProps) {
  const mount = useRef<HTMLDivElement>(null);
  const actionRef = useRef<CharacterAnimation>('IDLE');
  const actionStartedAt = useRef(0);
  const sceneApi = useRef<{ camera: Three.PerspectiveCamera; controls: OrbitControls } | null>(null);
  const [webglReady, setWebglReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [activeAction, setActiveAction] = useState<CharacterAnimation>('IDLE');

  const play = (action: CharacterAnimation) => {
    actionRef.current = action;
    actionStartedAt.current = performance.now();
    setActiveAction(action);
  };

  const resetView = () => {
    const api = sceneApi.current;
    if (!api) return;
    api.camera.position.set(0, 1.75, 5.1);
    api.controls.target.set(0, 1.32, 0);
    api.controls.update();
  };

  const zoom = (factor: number) => {
    const api = sceneApi.current;
    if (!api) return;
    const offset = api.camera.position.clone().sub(api.controls.target);
    const distance = Math.min(7, Math.max(3.15, offset.length() * factor));
    api.camera.position.copy(api.controls.target).add(offset.setLength(distance));
    api.controls.update();
  };

  useEffect(() => {
    const element = mount.current;
    if (!element) return;
    let cancelled = false;
    let cleanup = () => undefined;
    setWebglReady(false);
    setLoadError(false);

    void (async () => {
      const THREE = await import('three');
      const [{ OrbitControls }, { GLTFLoader }, { MeshoptDecoder }] = await Promise.all([
        import('three/addons/controls/OrbitControls.js'),
        import('three/addons/loaders/GLTFLoader.js'),
        import('three/addons/libs/meshopt_decoder.module.js')
      ]);
      if (cancelled) return;

      const selectedPalette = palette[appearanceVariant];
      const accentColor = classAccent[characterClass];
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let renderer: Three.WebGLRenderer;
      try {
        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
      } catch {
        setLoadError(true);
        return;
      }

      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.6));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.08;
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.domElement.className = 'character-3d-canvas';
      element.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
      camera.position.set(0, 1.75, 5.1);
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.07;
      controls.enablePan = false;
      controls.minDistance = 3.15;
      controls.maxDistance = 7;
      controls.minPolarAngle = Math.PI * 0.24;
      controls.maxPolarAngle = Math.PI * 0.68;
      controls.target.set(0, 1.32, 0);
      controls.update();
      sceneApi.current = { camera, controls };

      scene.add(new THREE.HemisphereLight(0xffffff, selectedPalette.secondary, 2.5));
      const keyLight = new THREE.DirectionalLight(0xfffbf4, 3.7);
      keyLight.position.set(3.2, 5, 4.2);
      keyLight.castShadow = true;
      keyLight.shadow.mapSize.set(1024, 1024);
      scene.add(keyLight);
      const rimLight = new THREE.DirectionalLight(accentColor, 2.2);
      rimLight.position.set(-3, 2.7, -2.4);
      scene.add(rimLight);

      const platform = new THREE.Mesh(
        new THREE.CylinderGeometry(1.52, 1.68, 0.2, 64),
        new THREE.MeshStandardMaterial({ color: selectedPalette.secondary, roughness: 0.84, metalness: 0.02 })
      );
      platform.position.y = -0.11;
      platform.receiveShadow = true;
      scene.add(platform);
      const platformRing = new THREE.Mesh(
        new THREE.TorusGeometry(1.22, 0.025, 8, 64),
        new THREE.MeshStandardMaterial({ color: accentColor, emissive: accentColor, emissiveIntensity: 0.15, roughness: 0.5 })
      );
      platformRing.rotation.x = Math.PI / 2;
      platformRing.position.y = 0.005;
      scene.add(platformRing);

      const avatar = new THREE.Group();
      const companion = new THREE.Group();
      scene.add(avatar, companion);
      const loader = new GLTFLoader();
      loader.setMeshoptDecoder(MeshoptDecoder);
      const loadModel = (url: string) => new Promise<Three.Object3D>((resolve, reject) => {
        loader.load(url, gltf => resolve(gltf.scene), undefined, reject);
      });

      const fitModel = (model: Three.Object3D, height: number, x: number, z: number) => {
        model.traverse(node => {
          if (node instanceof THREE.Mesh) {
            node.castShadow = true;
            node.receiveShadow = true;
          }
        });
        const initialBox = new THREE.Box3().setFromObject(model);
        const initialSize = initialBox.getSize(new THREE.Vector3());
        model.scale.setScalar(height / Math.max(initialSize.y, 0.001));
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        model.position.x += x - center.x;
        model.position.y += 0.01 - box.min.y;
        model.position.z += z - center.z;
      };

      try {
        const [heroModel, mochiModel] = await Promise.all([
          loadModel(modelByClass[characterClass]),
          loadModel('/assets/models/mochi.glb')
        ]);
        if (cancelled) {
          renderer.dispose();
          renderer.domElement.remove();
          return;
        }
        fitModel(heroModel, 2.72, -0.18, 0);
        fitModel(mochiModel, 0.76, 1.02, 0.22);
        avatar.add(heroModel);
        companion.add(mochiModel);
        setWebglReady(true);
      } catch {
        setLoadError(true);
      }

      const motes = new THREE.Group();
      const moteMaterial = new THREE.MeshStandardMaterial({ color: accentColor, emissive: accentColor, emissiveIntensity: 0.22, roughness: 0.45 });
      const moteMeshes: Three.Mesh[] = [];
      for (let index = 0; index < 9; index += 1) {
        const mote = new THREE.Mesh(new THREE.OctahedronGeometry(index % 2 ? 0.036 : 0.052), moteMaterial);
        const angle = index * 1.74;
        const radius = 1.3 + (index % 3) * 0.16;
        mote.position.set(Math.cos(angle) * radius, 0.55 + (index % 5) * 0.42, Math.sin(angle) * radius * 0.42);
        motes.add(mote);
        moteMeshes.push(mote);
      }
      scene.add(motes);

      const resize = () => {
        const width = Math.max(1, element.clientWidth);
        const height = Math.max(1, element.clientHeight);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height, false);
      };
      const observer = new ResizeObserver(resize);
      observer.observe(element);
      resize();

      const clock = new THREE.Clock();
      let frame = 0;
      const animate = () => {
        const elapsed = clock.getElapsedTime();
        const motion = prefersReducedMotion ? 0 : 1;
        const action = actionRef.current;
        const actionAge = performance.now() - actionStartedAt.current;
        const actionProgress = Math.min(1, actionAge / 2100);

        avatar.position.y = Math.sin(elapsed * 2.05) * 0.025 * motion;
        avatar.rotation.set(0, Math.sin(elapsed * 0.62) * 0.055 * motion, Math.sin(elapsed * 1.25) * 0.01 * motion);
        companion.position.y = Math.sin(elapsed * 2.5 + 0.8) * 0.032 * motion;
        companion.rotation.set(0, -0.12 + Math.sin(elapsed * 0.8) * 0.08 * motion, Math.sin(elapsed * 1.9) * 0.025 * motion);

        if (action === 'WAVE') {
          avatar.rotation.z = Math.sin(elapsed * 8) * 0.085 * motion;
          avatar.rotation.y = -0.16 + Math.sin(elapsed * 4) * 0.12 * motion;
          companion.rotation.z = Math.sin(elapsed * 10) * 0.1 * motion;
        } else if (action === 'CELEBRATE') {
          const bounce = Math.abs(Math.sin(actionProgress * Math.PI * 3)) * 0.18 * motion;
          avatar.position.y += bounce;
          avatar.rotation.y = actionProgress * Math.PI * 2 * motion;
          companion.position.y += Math.abs(Math.sin(actionProgress * Math.PI * 5)) * 0.12 * motion;
          platformRing.rotation.z = elapsed * 2.8 * motion;
        } else {
          platformRing.rotation.z = 0;
        }

        if (action !== 'IDLE' && actionAge > 2100) {
          actionRef.current = 'IDLE';
          setActiveAction('IDLE');
        }
        motes.rotation.y = elapsed * 0.17 * motion;
        moteMeshes.forEach((mote, index) => { mote.position.y += Math.sin(elapsed * 1.7 + index) * 0.0012 * motion; });
        controls.update();
        renderer.render(scene, camera);
        frame = requestAnimationFrame(animate);
      };
      animate();

      const disposeMaterial = (material: Three.Material) => {
        Object.values(material).forEach(value => {
          if (value && typeof value === 'object' && 'isTexture' in value && value.isTexture) value.dispose();
        });
        material.dispose();
      };
      cleanup = () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
        controls.dispose();
        scene.traverse(node => {
          if (node instanceof THREE.Mesh) {
            node.geometry.dispose();
            const materials = Array.isArray(node.material) ? node.material : [node.material];
            materials.forEach(disposeMaterial);
          }
        });
        renderer.dispose();
        renderer.domElement.remove();
        sceneApi.current = null;
      };
    })();

    return () => { cancelled = true; cleanup(); };
  }, [appearanceVariant, characterClass]);

  return <section class={`character-3d ${webglReady ? 'is-ready' : ''}`} aria-label="Interactive 3D character viewer">
    <div class="character-3d-stage" ref={mount}>
      <img class="character-3d-fallback" src={fallbackImage} alt="Your character" />
      {!webglReady && !loadError ? <span class="character-3d-status"><LoaderCircle class="spin" size={18} /> Loading 3D hero</span> : null}
      {loadError ? <span class="character-3d-status is-error"><AlertCircle size={18} /> 3D model unavailable</span> : null}
      <span class="character-3d-level" aria-hidden="true"><small>LV</small>{level}</span>
      <span class="character-3d-tip"><Hand size={14} /> Drag to turn · scroll to zoom</span>
    </div>
    <div class="character-3d-actions" role="group" aria-label="Character animations and camera controls">
      <button type="button" class={activeAction === 'IDLE' ? 'is-active' : ''} aria-pressed={activeAction === 'IDLE'} onClick={() => play('IDLE')}><Sparkles size={15} /> Idle</button>
      <button type="button" class={activeAction === 'WAVE' ? 'is-active' : ''} aria-pressed={activeAction === 'WAVE'} onClick={() => play('WAVE')}><Hand size={15} /> Wave</button>
      <button type="button" class={activeAction === 'CELEBRATE' ? 'is-active' : ''} aria-pressed={activeAction === 'CELEBRATE'} onClick={() => play('CELEBRATE')}><PartyPopper size={15} /> Celebrate</button>
      <span class="character-3d-camera" aria-label="Camera controls"><button type="button" aria-label="Zoom out" onClick={() => zoom(1.18)}><ZoomOut size={16} /></button><button type="button" aria-label="Reset view" onClick={resetView}><RotateCcw size={16} /></button><button type="button" aria-label="Zoom in" onClick={() => zoom(.84)}><ZoomIn size={16} /></button></span>
    </div>
  </section>;
}
